import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph } from '@digdir/designsystemet-react';
import type { FactorDimension } from '../../../api/hms';
import type { DimensionSummary } from '../../../api/workEnvironmentTypes';
import {
  BALANCE_RANGE,
  balancePosition,
  barPercent,
  dimensionBalance,
  dimensionColumns,
  oneDecimal,
  signedDecimal,
} from '../../WorkEnvironmentSurveyDetail/riskView';
import { balanceNote, movementText } from '../reportView';
import { ReportSection } from './ReportSection';
import classes from '../WorkEnvironmentReport.module.css';

type Dimensions = Record<FactorDimension, DimensionSummary | null>;

type Props = {
  dimensions: Dimensions;
  previous: { dimensions: Dimensions; measuredAt: string } | null;
};

const DIMENSION_TEXTS: Record<FactorDimension, string> = {
  LOAD: 'workEnvironmentReport.jdr.loads',
  RESOURCE: 'workEnvironmentReport.jdr.resources',
};

/** The balance between demands and resources first, then each area as a bar */
export const JdrSection = ({ dimensions, previous }: Props): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const balance = dimensionBalance(dimensions);
  const movement = movementText(dimensions, previous, t, i18n.language);
  const columns = dimensionColumns(dimensions);
  if (columns.length === 0) return null;
  const value = balance !== null ? signedDecimal(balance, i18n.language) : '';

  return (
    <ReportSection section="jdr" intro={t('workEnvironmentReport.jdr.intro')}>
      <div className={classes.card}>
        {balance !== null && (
          <div className={classes.balanceRow}>
            <p className={classes.balanceValue}>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                {t('workEnvironmentReport.jdr.balance')}
              </span>
              <span className="ds-heading" data-size="lg" data-weighs={balance >= 0 ? 'resources' : 'loads'}>
                {value}
              </span>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                {balanceNote(balance, t)}
              </span>
            </p>
            <div className={classes.balance}>
              <span
                role="img"
                aria-label={t('workEnvironmentReport.jdr.balanceLabel', { value, range: BALANCE_RANGE })}
                className={classes.balanceStripe}
              >
                <span
                  className={classes.balanceMarker}
                  data-weighs={balance >= 0 ? 'resources' : 'loads'}
                  style={{ left: balancePosition(balance) }}
                />
              </span>
              <span aria-hidden="true" className={`ds-paragraph ${classes.balanceScale}`} data-size="xs">
                <span>{t('workEnvironmentReport.jdr.weighs.loads')}</span>
                <span>{t('workEnvironmentReport.jdr.weighs.resources')}</span>
              </span>
            </div>
          </div>
        )}
        {movement && (
          <Paragraph data-size="sm" className={classes.subtle}>
            {movement}
          </Paragraph>
        )}
        <div className={classes.bars}>
          {columns.map(({ dimension, summary }) => (
            <div key={dimension} className={classes.barColumn} data-kind={dimension}>
              <Heading level={3} data-size="2xs">
                {t(DIMENSION_TEXTS[dimension])}
              </Heading>
              <ul>
                {summary.areas.map((area) => (
                  <li key={area.key}>
                    <span className={classes.barLabel}>
                      <span className="ds-paragraph" data-size="sm">
                        {area.name}
                      </span>
                      <span className={`ds-label ${classes.barValue}`} data-size="sm">
                        {oneDecimal(area.score, i18n.language)}
                      </span>
                    </span>
                    <span className={classes.bar} aria-hidden="true">
                      <span style={{ width: barPercent(area.score) }} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </ReportSection>
  );
};
